'use client'

import { useState } from 'react'
import Image from 'next/image'
import { createSupabaseBrowser } from '@/lib/supabase-browser'
import { sendPasswordResetEmail } from '@/lib/auth-password-reset'

export default function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [resetLoading, setResetLoading] = useState(false)

  async function handleLogin(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    const submittedEmail = String(formData.get('email') ?? '').trim()
    const submittedPassword = String(formData.get('password') ?? '')
    setLoading(true)
    setError(null)
    setMessage(null)

    const supabase = createSupabaseBrowser()
    const { data, error: authError } = await supabase.auth.signInWithPassword({
      email: submittedEmail,
      password: submittedPassword,
    })

    if (authError) {
      setError(authError.message)
      setLoading(false)
      return
    }

    if (data.user) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('must_reset_password')
        .eq('id', data.user.id)
        .maybeSingle()

      if (profile?.must_reset_password === true) {
        window.location.href = '/account/reset-password'
        return
      }
    }

    window.location.href = '/'
  }

  async function handleForgotPassword() {
    setResetLoading(true)
    setError(null)
    setMessage(null)

    const supabase = createSupabaseBrowser()
    const result = await sendPasswordResetEmail(
      supabase,
      email,
      window.location.href,
    )

    if (result.ok) {
      setMessage('Password reset email sent. Check your inbox.')
    } else {
      setError(result.error)
    }

    setResetLoading(false)
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-background px-6 py-10"
      style={{
        paddingTop: 'max(2.5rem, env(safe-area-inset-top))',
        paddingBottom: 'max(2.5rem, env(safe-area-inset-bottom))',
      }}
    >
      <div className="w-full max-w-sm">
        {/* Icon + brand */}
        <div className="flex flex-col items-center mb-8">
          <div className="size-20 rounded-lg bg-primary flex items-center justify-center shadow-[0_8px_24px_rgba(62,207,142,0.22)] mb-4">
            <Image
              src="/icons/icon-192.png"
              alt="Westline"
              width={72}
              height={72}
              className="rounded-md"
              priority
            />
          </div>
          <h1 className="text-[28px] font-medium text-notion-text tracking-tight">Westline</h1>
          <p className="text-[14px] text-notion-text-muted mt-1">Sign in to your sales workspace</p>
        </div>

        {/* Form */}
        <form onSubmit={handleLogin} className="space-y-3">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md px-4 py-2.5 text-[13px] text-signal-red">
              {error}
            </div>
          )}
          {message && (
            <div className="bg-green-50 border border-green-200 rounded-md px-4 py-2.5 text-[13px] text-green-700">
              {message}
            </div>
          )}

          <input
            data-testid="login-email"
            name="email"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="Email"
            required
            autoFocus
            autoComplete="email"
            className="w-full px-4 py-3 bg-white border border-notion-border rounded-md text-[15px] text-notion-text placeholder:text-notion-text-muted outline-none focus:border-primary-deep focus:ring-3 focus:ring-primary/20 transition-all"
          />

          <input
            data-testid="login-password"
            name="password"
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="Password"
            required
            autoComplete="current-password"
            className="w-full px-4 py-3 bg-white border border-notion-border rounded-md text-[15px] text-notion-text placeholder:text-notion-text-muted outline-none focus:border-primary-deep focus:ring-3 focus:ring-primary/20 transition-all"
          />

          <button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-primary text-primary-foreground rounded-md text-[15px] font-medium hover:bg-primary-deep active:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Signing in…' : 'Sign in'}
          </button>

          <div className="text-center pt-2">
            <button
              type="button"
              onClick={handleForgotPassword}
              disabled={resetLoading || loading}
              className="text-[13px] text-notion-text-muted hover:text-notion-text active:opacity-70 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {resetLoading ? 'Sending reset…' : 'Forgot password?'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
